import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const MIME_EXTENSION_MAP = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png'
};

const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const normalizeStatus = (value) => (value || 'uploaded').toLowerCase();
const sanitizeNamePart = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'unknown';
  return raw
    .replace(/[<>:"/\\|?*]/g, '')
    .replace(/\s+/g, '_');
};

const getExtensionFromName = (value) => {
  if (!value) return '';
  const cleanValue = String(value).split('?')[0].split('#')[0];
  const idx = cleanValue.lastIndexOf('.');
  if (idx === -1) return '';
  return cleanValue.slice(idx + 1).toLowerCase();
};

const getExtensionFromDisposition = (contentDisposition) => {
  if (!contentDisposition) return '';
  const match = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
  if (!match || !match[1]) return '';
  return getExtensionFromName(decodeURIComponent(match[1].replace(/"/g, '')));
};

function EmployeeDocuments() {
  const { id } = useParams();
  const [documents, setDocuments] = useState([]);
  const [employeeName, setEmployeeName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchEmployeeDocuments();
  }, [id]);

  const fetchEmployeeDocuments = async () => {
    setLoading(true);
    setError('');
    try {
      const endpoints = [
        `${API_URL}/documents/employee/${id}`,
        `${API_URL}/employees/${id}/documents`
      ];

      let docs = null;
      for (const endpoint of endpoints) {
        try {
          const response = await axios.get(endpoint, getAuthConfig());
          const payload = response.data?.documents || response.data?.data || response.data;
          docs = Array.isArray(payload) ? payload : [];
          break;
        } catch (err) {
          if (err?.response?.status !== 404 && err?.response?.status !== 405) {
            throw err;
          }
        }
      }

      if (docs === null) {
        const allResponse = await axios.get(`${API_URL}/documents/all`, getAuthConfig());
        const allDocs = Array.isArray(allResponse.data) ? allResponse.data : [];
        docs = allDocs.filter((doc) => {
          const docEmployeeId = doc.employee_id || doc.employeeId || doc.employee?.id;
          return String(docEmployeeId) === String(id);
        });
      }

      setDocuments(docs);
      if (docs.length > 0) {
        setEmployeeName(docs[0].employee_name || docs[0].employee?.name || '');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load employee documents.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API_URL}/documents/${doc.id}/download?id=${id}`, {
        ...getAuthConfig(),
        responseType: 'blob'
      });
      const contentType = response.headers?.['content-type'] || '';
      const contentDisposition = response.headers?.['content-disposition'] || '';
      const extension =
        getExtensionFromName(doc.file_name || doc.filename || doc.original_name || doc.path) ||
        getExtensionFromDisposition(contentDisposition) ||
        MIME_EXTENSION_MAP[contentType.toLowerCase()] ||
        'pdf';
      const employeeUserId = sanitizeNamePart(
        response.headers?.['x-employee-userid'] ||
        response.headers?.['X-Employee-UserId'] ||
        doc.employee_user_id ||
        doc.employee_id
      );
      const documentTypePart = sanitizeNamePart(doc.document_type);
      const downloadName = `${employeeUserId}_${documentTypePart}.${extension}`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.message || 'Download failed.');
    }
  };

  const handleVerify = async (docId) => {
    try {
      await axios.put(`${API_URL}/documents/${docId}/verify`, {}, getAuthConfig());
      await fetchEmployeeDocuments();
      alert('Document verified successfully.');
    } catch (err) {
      alert(err.response?.data?.message || 'Verification failed.');
    }
  };

  const handleReject = async (docId) => {
    const rejectionReason = window.prompt('Enter rejection reason');
    if (!rejectionReason || !rejectionReason.trim()) return;

    try {
      await axios.put(
        `${API_URL}/documents/${docId}/reject`,
        { rejectionReason: rejectionReason.trim() },
        getAuthConfig()
      );
      await fetchEmployeeDocuments();
      alert('Document rejected successfully.');
    } catch (err) {
      alert(err.response?.data?.message || 'Rejection failed.');
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Employee Documents</h1>
        <p>
          {employeeName ? `${employeeName} (${id})` : `Employee ID: ${id}`}
        </p>
      </div>

      <div className="section-card">
        <div className="header-actions">
          <h2>Documents ({documents.length})</h2>
          <div className="btn-group">
            <button type="button" className="btn btn-secondary" onClick={fetchEmployeeDocuments}>
              Refresh
            </button>
            <Link to="/employees" className="btn btn-primary link-btn">
              Back to Employees
            </Link>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {documents.length === 0 ? (
          <p style={{ color: '#999' }}>No documents found for this employee.</p>
        ) : (
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Document Type</th>
                  <th>Document Number</th>
                  <th>Status</th>
                  <th>Uploaded At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => {
                  const status = normalizeStatus(doc.status);
                  return (
                    <tr key={doc.id}>
                      <td>{doc.id}</td>
                      <td>{doc.document_type || '-'}</td>
                      <td>{doc.document_number || '-'}</td>
                      <td>
                        <span className={`badge ${status}`}>
                          {status}
                        </span>
                      </td>
                      <td>
                        {doc.created_at ? new Date(doc.created_at).toLocaleString() : '-'}
                      </td>
                      <td>
                        <div className="btn-group" style={{ gap: '6px' }}>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => handleDownload(doc)}
                          >
                            View
                          </button>
                          {status === 'uploaded' && (
                            <>
                              <button
                                type="button"
                                className="btn btn-success btn-sm"
                                onClick={() => handleVerify(doc.id)}
                              >
                                Verify
                              </button>
                              <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => handleReject(doc.id)}
                              >
                                Reject
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default EmployeeDocuments;
